'use client'

import { categoryByKey, MIN_TOP } from '../_lib/data'
import type { SpendItem } from '../types'
import { OrderedTile } from './OrderedTile'

export function TopStep({
  items,
  picked,
  top,
  onRank,
  onUnrank,
  onNudge,
  onBack,
  onNext,
}: {
  items: SpendItem[]
  picked: Record<string, true>
  top: string[]
  onRank: (id: string) => void
  onUnrank: (id: string) => void
  onNudge: (id: string, direction: -1 | 1) => void
  onBack: () => void
  onNext: () => void
}) {
  const byId = Object.fromEntries(items.map((it) => [it.id, it]))
  const remaining = items.filter((it) => picked[it.id] && !top.includes(it.id))
  const blocked = top.length < MIN_TOP

  return (
    <div>
      <h1 className="text-title-md text-gray-darker">
        What did you spend the <em className="text-green-darker">most</em> on?
      </h1>
      <p className="mt-3 max-w-150 text-body text-neutral-darker">
        From your own list, choose at least three expenses you spent the most on over the last few months.
      </p>
      <p className="mt-2 max-w-150 text-body-sm text-neutral-darker">
        Tip: Look through credit card statements, bank account histories and a summary from a budgeting app for more
        accurate insights, but a rough guess from memory works too.
      </p>

      <div className="mt-6 grid gap-4 tablet:grid-cols-2 tablet:items-start">
        <section>
          <h2 className="mb-2 flex items-baseline justify-between text-body-xs font-bold text-neutral-darker uppercase">
            Everything you spend on
            <span className="tabular-nums">{remaining.length ? `${remaining.length} left` : ''}</span>
          </h2>
          <div className="flex min-h-45 flex-wrap content-start gap-2 rounded-2xl bg-bone-warm p-2.5">
            {remaining.map((it) => (
              <button
                key={it.id}
                type="button"
                onClick={() => onRank(it.id)}
                aria-label={`Add ${it.t} to your biggest expenses`}
                className="rounded-full border border-smoke bg-card px-3.5 py-2 text-body-sm text-neutral-darker hover:border-green-darker hover:text-green-darker"
              >
                {it.t}
              </button>
            ))}
            {remaining.length === 0 && (
              <p className="w-full p-4 text-center text-body-sm text-neutral-darker">
                Everything you picked is in your top list.
              </p>
            )}
          </div>
        </section>

        <section>
          <h2 className="mb-2 flex items-baseline justify-between text-body-xs font-bold text-neutral-darker uppercase">
            Biggest first
            <span className="tabular-nums">{top.length ? `${top.length} chosen` : ''}</span>
          </h2>
          <ul className="flex min-h-45 flex-col gap-2 rounded-2xl bg-bone-warm p-2.5">
            {top.map((id, i) => {
              const item = byId[id]
              if (!item) return null
              return (
                <OrderedTile
                  key={id}
                  rank={i + 1}
                  title={item.t}
                  subtitle={categoryByKey(item.cat).n}
                  isFirst={i === 0}
                  isLast={i === top.length - 1}
                  onMoveUp={() => onNudge(id, -1)}
                  onMoveDown={() => onNudge(id, 1)}
                  onRemove={() => onUnrank(id)}
                  removeLabel={`Remove ${item.t} from your biggest expenses`}
                />
              )
            })}
            {top.length === 0 && (
              <li className="p-4 text-center text-body-sm text-neutral-darker">
                Tap an expense on the left to move it here.
              </li>
            )}
          </ul>
        </section>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-4">
        <button type="button" onClick={onBack} className="text-body-sm-bold text-green-dark underline">
          Back
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={blocked}
          className="rounded-full bg-green-lighter-2 px-7 py-3.5 text-body-sm-bold text-black uppercase disabled:opacity-45"
        >
          See your results
        </button>
        {blocked && <p className="text-body-sm text-neutral-darker">Choose at least {MIN_TOP}.</p>}
      </div>
    </div>
  )
}
